import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Search, X, TrendingUp, Upload, Trash2, Video as VideoIcon, Image as ImageIcon, Plus, Star, RefreshCw, Pencil } from "lucide-react";
import ExerciseIcon, { MuscleChip } from "@/components/ExerciseIcon";
import MuscleMap from "@/components/MuscleMap";
import CustomExerciseModal from "@/components/CustomExerciseModal";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MUSCLE_FILTERS = ["chest", "back", "lats", "shoulders", "biceps", "triceps", "quads", "glutes", "hamstrings", "core"];
const EQUIPMENT_FILTERS = ["barbell", "dumbbells", "bodyweight", "cable", "machine", "kettlebell"];

// Same synonym map that lives on the backend — used to compute per-group
// counts on the client without spawning 10 extra API round-trips.
const MUSCLE_SYNONYMS = {
  chest: ["chest", "upper chest"],
  back: ["lats", "upper back", "mid-back", "lower back", "rhomboids"],
  lats: ["lats"],
  shoulders: ["shoulders", "front delts", "side delts", "rear delts"],
  biceps: ["biceps", "brachialis", "forearms"],
  triceps: ["triceps"],
  quads: ["quads"],
  glutes: ["glutes"],
  hamstrings: ["hamstrings"],
  core: ["core", "abs", "obliques", "hip flexors"],
  calves: ["calves"],
};

const mediaFileUrl = (exerciseId, mediaId) =>
  `${BACKEND}/api/exercises/${exerciseId}/media/${mediaId}/file`;

export default function Exercises() {
  const { user } = useAuth();
  const isStaff = !!(user?.is_admin || user?.is_coach);
  const [items, setItems] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [q, setQ] = useState("");
  const [muscle, setMuscle] = useState("");
  const [equipment, setEquipment] = useState("");
  const [selected, setSelected] = useState(null);
  const [topCues, setTopCues] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const r = await api.get("/exercises", { params: { q, muscle, equipment } });
    setItems(r.data);
  };
  // Pull the full unfiltered set exactly once so the muscle-map counts stay
  // accurate no matter what filter is active.
  useEffect(() => {
    api.get("/exercises").then((r) => setAllExercises(r.data || [])).catch(() => {});
  }, []);

  const muscleCounts = useMemo(() => {
    const out = {};
    Object.entries(MUSCLE_SYNONYMS).forEach(([group, syns]) => {
      const lower = syns.map((s) => s.toLowerCase());
      out[group] = allExercises.filter((e) =>
        (e.muscles || []).some((m) => {
          const ml = m.toLowerCase();
          return lower.includes(ml) || lower.some((s) => ml.includes(s));
        })
      ).length;
    });
    return out;
  }, [allExercises]);
  const loadTopCues = async () => {
    try {
      const r = await api.get("/exercises/top-cues", { params: { limit: 5 } });
      setTopCues(r.data?.items || []);
    } catch {
      setTopCues([]);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, muscle, equipment]);
  useEffect(() => {
    loadTopCues();
  }, []);

  // Deep-link: ?id=<exercise_id> auto-opens the detail modal once exercises load.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || !items.length) return;
    const hit = items.find((e) => e.id === id);
    if (hit) setSelected(hit);
  }, [searchParams, items]);

  // Emit tap analytics whenever a card is opened. Source tells us where the
  // user came from so we can tune the AI Coach prompt later.
  useEffect(() => {
    if (!selected) return;
    const source = searchParams.get("id") ? "history" : "library";
    api.post(`/exercises/${selected.id}/tap`, { source }).catch(() => {});
    setTimeout(loadTopCues, 600);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeModal = () => {
    setSelected(null);
    if (searchParams.get("id")) {
      searchParams.delete("id");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-blaze">/ Exercise library</div>
          <h1 className="font-heading text-5xl uppercase mt-1">Form first.</h1>
          <p className="text-zinc-400 text-sm mt-2">Curated compound + accessory lifts with step-by-step instructions and form cues.</p>
        </div>
        {isStaff && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            data-testid="add-custom-exercise"
            className="blaze-btn px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2"
          >
            <Plus className="h-3 w-3" /> Add exercise
          </button>
        )}
      </div>

      <div className="obs-card">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="exercises-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or muscle..."
              className="w-full bg-obsidian-900 border border-obsidian-600 pl-9 pr-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
            />
          </div>
          <select value={muscle} onChange={(e) => setMuscle(e.target.value)} className="bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm text-sm">
            <option value="">All muscles</option>
            {MUSCLE_FILTERS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={equipment} onChange={(e) => setEquipment(e.target.value)} className="bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm text-sm">
            <option value="">All equipment</option>
            {EQUIPMENT_FILTERS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <MuscleMap active={muscle} onSelect={setMuscle} counts={muscleCounts} />

      {topCues.length > 0 && (
        <div className="obs-card" data-testid="top-cues-widget">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">Your most-opened cues</div>
              <h3 className="font-heading text-2xl uppercase">Top form references</h3>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {topCues.map((c) => {
              const ex = items.find((e) => e.id === c.exercise_id);
              return (
                <button
                  key={c.exercise_id}
                  data-testid={`top-cue-${c.exercise_id}`}
                  onClick={() => ex && setSelected(ex)}
                  disabled={!ex}
                  className="flex items-center gap-2 border border-obsidian-600 rounded-sm p-2 hover:border-blaze transition-colors text-left disabled:opacity-50"
                >
                  <ExerciseIcon exercise={c} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-zinc-200 truncate">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-blaze">{c.taps} tap{c.taps === 1 ? "" : "s"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercises-grid">
        {items.length === 0 ? (
          <div className="col-span-full text-zinc-500 uppercase tracking-widest text-sm text-center py-10">No matches.</div>
        ) : items.map((ex) => (
          <button
            key={ex.id}
            data-testid={`exercise-card-${ex.id}`}
            onClick={() => setSelected(ex)}
            className="obs-card text-left !p-0 overflow-hidden hover:border-blaze transition-colors"
          >
            <div className="relative">
              <ExerciseIcon exercise={ex} size="hero" />
              <div className="absolute top-2 right-2 px-2 py-0.5 bg-obsidian-900/90 border border-obsidian-600 text-[10px] uppercase tracking-widest rounded-sm">
                {ex.difficulty}
              </div>
              {ex.source === "custom" && (
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-blaze/90 text-white text-[10px] uppercase tracking-widest rounded-sm inline-flex items-center gap-1">
                  <Star className="h-2.5 w-2.5" /> {ex.is_global ? "Global" : "Coach"}
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-heading text-xl uppercase">{ex.name}</h3>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                {(ex.muscles || []).slice(0, 3).join(" · ")} · {ex.equipment}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <ExerciseDetailModal
          exercise={selected}
          onClose={closeModal}
          isStaff={isStaff}
          isAdmin={!!user?.is_admin}
          mediaFileUrl={mediaFileUrl}
          onDeleted={() => { setSelected(null); load(); }}
        />
      )}

      {showAddModal && (
        <CustomExerciseModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

function ExerciseDetailModal({ exercise, onClose, isStaff, isAdmin, mediaFileUrl, onDeleted }) {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);
  const [imgBump, setImgBump] = useState(0);
  const [details, setDetails] = useState({
    steps: exercise.steps || [],
    cues: exercise.cues || [],
    description: exercise.description || "",
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const fileRef = React.useRef(null);
  const isCustom = exercise?.source === "custom";

  const startEdit = () => {
    setDraft({
      steps: details.steps.join("\n"),
      cues: details.cues.join("\n"),
      description: details.description,
    });
    setEditing(true);
  };

  const saveDetails = async () => {
    const parse = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);
    setSavingDetails(true);
    try {
      const payload = {
        steps: parse(draft.steps),
        cues: parse(draft.cues),
        description: draft.description.trim(),
      };
      await api.put(`/exercises/${exercise.id}/details`, payload);
      setDetails(payload);
      setEditing(false);
      toast.success("Exercise details updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save details");
    } finally {
      setSavingDetails(false);
    }
  };

  const regenerateImage = async () => {
    if (!window.confirm("Generate a fresh illustration for this exercise? The current image will be replaced.")) return;
    setRegenBusy(true);
    try {
      await api.post(`/exercises/${exercise.id}/image/regenerate`, {}, { timeout: 90000 });
      setImgBump(Date.now()); // cache-bust so the new render shows immediately
      toast.success("New illustration generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image generation failed — try again");
    } finally {
      setRegenBusy(false);
    }
  };

  const loadMedia = async () => {
    try {
      const r = await api.get(`/exercises/${exercise.id}/media`);
      setMedia(r.data || []);
    } catch {
      setMedia([]);
    }
  };
  useEffect(() => {
    loadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise.id]);

  const promote = async () => {
    if (!window.confirm("Make this exercise visible to every Ripple Effect user?")) return;
    try {
      await api.post(`/exercises/custom/${exercise.id}/promote`);
      toast.success("Promoted to global library");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to promote");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${exercise.name}" from the library?`)) return;
    try {
      await api.delete(`/exercises/custom/${exercise.id}`);
      toast.success("Exercise removed");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caption.trim()) fd.append("caption", caption.trim());
      await api.post(`/exercises/${exercise.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Media uploaded");
      setCaption("");
      loadMedia();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = async (mediaId) => {
    if (!window.confirm("Delete this media? Clients will no longer see it.")) return;
    try {
      await api.delete(`/exercises/${exercise.id}/media/${mediaId}`);
      loadMedia();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4"
      data-testid="exercise-detail"
      onClick={onClose}
    >
      <div
        className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <ExerciseIcon key={imgBump} exercise={exercise} size="hero-tall" bump={imgBump} />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 bg-obsidian-900/90 rounded-sm text-zinc-300 hover:text-white"
            data-testid="exercise-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
          {isStaff && (
            <button
              type="button"
              onClick={regenerateImage}
              disabled={regenBusy}
              data-testid="exercise-regenerate-image"
              title="Replace a defective illustration with a fresh AI render"
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-obsidian-900/90 border border-obsidian-600 rounded-sm text-[10px] uppercase tracking-widest text-zinc-300 hover:text-blaze hover:border-blaze disabled:opacity-60"
            >
              <RefreshCw className={`h-3 w-3 ${regenBusy ? "animate-spin" : ""}`} />
              {regenBusy ? "Rendering…" : "Regenerate image"}
            </button>
          )}
        </div>
        <div className="p-6 space-y-6">
          <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(exercise.muscles || []).map((m) => (
                <MuscleChip key={m} muscle={m} />
              ))}
              {isCustom && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/40 text-blaze bg-blaze/10 rounded-full inline-flex items-center gap-1">
                  <Star className="h-2.5 w-2.5" /> {exercise.is_global ? "Global" : `Added by ${exercise.coach_name || "coach"}`}
                </span>
              )}
            </div>
            <h2 className="font-heading text-4xl uppercase">{exercise.name}</h2>
            <div className="text-[11px] text-zinc-500 uppercase tracking-widest mt-1">
              {exercise.equipment} · {exercise.difficulty}
            </div>
            {details.description && !editing && (
              <p className="text-sm text-zinc-300 mt-3">{details.description}</p>
            )}
            {isCustom && exercise.video_url && (
              <a
                href={exercise.video_url}
                target="_blank"
                rel="noreferrer"
                data-testid="custom-ex-video-link"
                className="inline-flex items-center gap-2 mt-3 text-[11px] uppercase tracking-widest text-blaze hover:underline"
              >
                <VideoIcon className="h-3 w-3" /> Watch demo video
              </a>
            )}
            {isCustom && isStaff && (
              <div className="flex flex-wrap gap-2 mt-4">
                {isAdmin && !exercise.is_global && (
                  <button
                    type="button"
                    onClick={promote}
                    data-testid="custom-ex-promote"
                    className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                  >
                    <Star className="h-3 w-3" /> Promote to global
                  </button>
                )}
                <button
                  type="button"
                  onClick={remove}
                  data-testid="custom-ex-delete"
                  className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            )}
          </div>

          {isStaff && !editing && (
            <button
              type="button"
              onClick={startEdit}
              data-testid="exercise-edit-details"
              className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
            >
              <Pencil className="h-3 w-3" /> Edit details
            </button>
          )}

          {editing ? (
            <div className="space-y-4" data-testid="exercise-details-editor">
              <div>
                <div className="overline mb-1.5">Description</div>
                <textarea
                  data-testid="edit-description"
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  rows={2}
                  placeholder="Short description of the movement"
                  className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
                />
              </div>
              <div>
                <div className="overline mb-1.5">Step-by-step (one step per line)</div>
                <textarea
                  data-testid="edit-steps"
                  value={draft.steps}
                  onChange={(e) => setDraft((d) => ({ ...d, steps: e.target.value }))}
                  rows={6}
                  placeholder={"Set your feet shoulder width\nBrace your core\n..."}
                  className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
                />
              </div>
              <div>
                <div className="overline mb-1.5">Form cues (one cue per line)</div>
                <textarea
                  data-testid="edit-cues"
                  value={draft.cues}
                  onChange={(e) => setDraft((d) => ({ ...d, cues: e.target.value }))}
                  rows={4}
                  placeholder={"Chest up\nDrive through the heels"}
                  className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="edit-details-save"
                  onClick={saveDetails}
                  disabled={savingDetails}
                  className="blaze-btn px-4 py-2 text-xs disabled:opacity-50"
                >
                  {savingDetails ? "Saving…" : "Save details"}
                </button>
                <button
                  type="button"
                  data-testid="edit-details-cancel"
                  onClick={() => setEditing(false)}
                  className="ghost-btn px-4 py-2 text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
          {(details.steps || []).length > 0 && (
            <div>
              <div className="overline mb-2">Step-by-step</div>
              <ol className="space-y-2">
                {details.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-200">
                    <span className="font-data text-blaze font-bold w-6 shrink-0">{i + 1}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(details.cues || []).length > 0 && (
            <div>
              <div className="overline mb-2">Form cues</div>
              <ul className="space-y-1.5">
                {details.cues.map((c, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex gap-2">
                    <span className="text-blaze mt-1 shrink-0">·</span> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
            </>
          )}

          {/* Coach / Admin uploaded media */}
          <div className="border-t border-obsidian-600 pt-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="overline">Coach media</div>
                <h3 className="font-heading text-xl uppercase">Photo & video demos</h3>
              </div>
              {isStaff && (
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={upload}
                    data-testid="ex-media-input"
                  />
                  <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="bg-obsidian-900 border border-obsidian-600 px-2 py-1.5 rounded-sm text-xs focus:border-blaze focus:outline-none w-48"
                    data-testid="ex-media-caption"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="blaze-btn px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="ex-media-upload"
                  >
                    <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload"}
                  </button>
                </div>
              )}
            </div>

            {media.length === 0 ? (
              <p className="text-xs text-zinc-500">
                {isStaff
                  ? "No demos yet — upload a photo or short video to help your clients nail form."
                  : "Your coach hasn't added any demos for this exercise yet."}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ex-media-grid">
                {media.map((m) => (
                  <div
                    key={m.id}
                    className="border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900/40 relative group"
                    data-testid={`ex-media-${m.id}`}
                  >
                    {m.kind === "video" ? (
                      <video
                        src={mediaFileUrl(exercise.id, m.id)}
                        controls
                        preload="metadata"
                        className="w-full h-40 object-cover bg-black"
                      />
                    ) : (
                      <a
                        href={mediaFileUrl(exercise.id, m.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="block"
                      >
                        <img
                          src={mediaFileUrl(exercise.id, m.id)}
                          alt={m.caption || "exercise media"}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      </a>
                    )}
                    <div className="p-2 text-[10px] text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                      {m.kind === "video" ? (
                        <VideoIcon className="h-3 w-3 text-blaze" />
                      ) : (
                        <ImageIcon className="h-3 w-3 text-blaze" />
                      )}
                      <span className="truncate flex-1">
                        {m.caption || `by ${m.uploaded_by_name || "coach"}`}
                      </span>
                      {isStaff && (
                        <button
                          type="button"
                          onClick={() => removeMedia(m.id)}
                          className="text-zinc-500 hover:text-blaze"
                          data-testid={`ex-media-delete-${m.id}`}
                          aria-label="Delete media"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
